import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createManualReturn, lookupOrder } from '@/api/returns';
import { fetchReasons } from '@/api/reasons';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { TextField } from '@/components/ui/TextField';
import { TextArea } from '@/components/ui/TextArea';
import { SelectField } from '@/components/ui/SelectField';
import { Toggle } from '@/components/ui/Toggle';
import { Badge } from '@/components/ui/Badge';
import { useAsyncData } from '@/hooks/useAsyncData';
import { toast } from '@/store/useToastStore';
import { REFUND_MODE_OPTIONS, refundModeLabel, resolveReturnReasons } from '@/constants/defaults';
import { formatCurrency, formatDate, humanizeEnum, toNumber } from '@/lib/format';
import { errorMessageOf } from '@/lib/errors';
import type {
  ManualReturnItem,
  ManualReturnRequest,
  RefundMode,
  ReturnReason,
  ShopifyLineItem,
  ShopifyOrder,
} from '@/types';
import { paths } from '@/router/paths';

/** Free-with-order items (tagged via a `freebie: true` line item property) can't be returned. */
function isFreebieLineItem(lineItem: ShopifyLineItem): boolean {
  return (lineItem.properties ?? []).some(
    (property) => property.name.toLowerCase() === 'freebie' && property.value.toLowerCase() === 'true',
  );
}

export function ManualReturnScreen() {
  const navigate = useNavigate();
  const { data: reasonData } = useAsyncData<ReturnReason[] | null>((signal) => fetchReasons(signal), []);
  const reasons = resolveReturnReasons(reasonData);

  const [orderNumber, setOrderNumber] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  /** All orders returned by the lookup (a customer may have several). */
  const [orderResults, setOrderResults] = useState<ShopifyOrder[]>([]);
  /** The order the seller picked to raise the return against. */
  const [order, setOrder] = useState<ShopifyOrder | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [isLookingUp, setIsLookingUp] = useState(false);

  const [selectedQuantities, setSelectedQuantities] = useState<Record<string, number>>({});
  const [reasonSlug, setReasonSlug] = useState('');
  const [refundMode, setRefundMode] = useState<RefundMode>('STORE_CREDIT');
  const [isExchange, setIsExchange] = useState(false);
  const [internalNotes, setInternalNotes] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleLookup(event: React.FormEvent) {
    event.preventDefault();
    if (!orderNumber.trim() && !email.trim() && !phone.trim()) {
      return;
    }
    setIsLookingUp(true);
    setLookupError(null);
    try {
      const found = await lookupOrder(
        orderNumber.trim() || undefined,
        email.trim() || undefined,
        phone.trim() || undefined,
      );
      const orders = Array.isArray(found) ? found : found ? [found] : [];
      if (orders.length === 0) {
        setLookupError('No order found with those details.');
        setOrderResults([]);
        setOrder(null);
        return;
      }
      setOrderResults(orders);
      // Single result → go straight to it; multiple → let the seller choose.
      setOrder(null);
      if (orders.length === 1) {
        selectOrder(orders[0]);
      }
    } catch (error) {
      setLookupError(errorMessageOf(error));
    } finally {
      setIsLookingUp(false);
    }
  }

  /** Pick one of the looked-up orders to raise the return against. */
  function selectOrder(selected: ShopifyOrder) {
    setOrder(selected);
    setSelectedQuantities({});
    setCustomerEmail(selected.email ?? selected.customer?.email ?? email.trim());
    setCustomerName(
      [selected.customer?.first_name, selected.customer?.last_name].filter(Boolean).join(' ').trim(),
    );
    setCustomerPhone(selected.customer?.phone ?? selected.shipping_address?.phone ?? phone.trim());
    if (reasons[0]) {
      setReasonSlug(reasons[0].slug);
    }
  }

  /** Return to the order list (only meaningful when multiple orders matched). */
  function clearSelection() {
    setOrder(null);
    setSelectedQuantities({});
  }

  function toggleItem(lineItemId: string) {
    setSelectedQuantities((current) => {
      const next = { ...current };
      if (next[lineItemId]) {
        delete next[lineItemId];
      } else {
        next[lineItemId] = 1;
      }
      return next;
    });
  }

  /** Clamp the return quantity for a line item to between 1 and the ordered qty. */
  function setItemQuantity(lineItemId: string, quantity: number, maxQuantity: number) {
    const clamped = Math.max(1, Math.min(quantity, maxQuantity));
    setSelectedQuantities((current) => ({ ...current, [lineItemId]: clamped }));
  }

  const selectedItems: ManualReturnItem[] = order
    ? (order.line_items ?? [])
        .filter((lineItem) => selectedQuantities[String(lineItem.id)] > 0)
        .map((lineItem) => {
          const item: ManualReturnItem = {
            imageUrl: lineItem.imageUrl ?? lineItem.image ?? undefined,
            shopifyLineItemId: String(lineItem.id),
            productTitle: lineItem.title,
            quantity: selectedQuantities[String(lineItem.id)],
            unitPrice: toNumber(lineItem.price),
          };
          if (lineItem.product_id != null) {
            item.shopifyProductId = String(lineItem.product_id);
          }
          if (lineItem.variant_id != null) {
            item.shopifyVariantId = String(lineItem.variant_id);
          }
          return item;
        })
    : [];

  const isEmailValid = email.trim() === '' || email.includes('@');

  const canSubmit =
    order !== null &&
    selectedItems.length > 0 &&
    reasonSlug !== '' &&
    customerEmail.trim() !== '';

  async function handleSubmit() {
    if (!order || !canSubmit) {
      return;
    }
    const payload: ManualReturnRequest = {
      shopifyOrderId: String(order.id),
      shopifyOrderNumber: order.name,
      customerEmail: customerEmail.trim(),
      customerName: customerName.trim() || undefined,
      customerPhone: customerPhone.trim() || undefined,
      refundMode,
      items: selectedItems,
      reasonSlug,
      isExchange,
      internalNotes: internalNotes.trim() || undefined,
    };
    setIsSubmitting(true);
    try {
      const created = await createManualReturn(payload);
      toast.success('Manual return created');
      if (created?.returnId) {
        navigate(paths.returnDetail(created.returnId));
      } else {
        navigate(paths.returns);
      }
    } catch (error) {
      toast.error(errorMessageOf(error));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="animate-fade-in">
      <PageHeader title="Create manual return" description="Raise a return on behalf of a customer." />

      <Card className="mb-4">
        <form onSubmit={handleLookup} className="flex flex-wrap items-start gap-3">
          <div className="min-w-44 flex-1">
            <TextField label="Order number" placeholder="#1234" value={orderNumber} onChange={(event) => setOrderNumber(event.target.value)} />
          </div>
          <div className="min-w-44 flex-1">
            <TextField
              label="Customer email"
              type="email"
              placeholder="findmyorder@gmail.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              errorText={email.trim() && !isEmailValid ? 'Enter a valid email' : undefined}
            />
          </div>
          <div className="min-w-44 flex-1">
            <TextField
              label="Phone number"
              type="tel"
              placeholder="9876543210"
              maxLength={10}
              inputMode="numeric"
              value={phone}
              onChange={(event) => setPhone(event.target.value.replace(/\D/g, '').slice(0, 10))}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="invisible text-sm font-medium">Find</span>
            <Button
              type="submit"
              isLoading={isLookingUp}
              disabled={(!orderNumber.trim() && !email.trim() && !phone.trim()) || !isEmailValid}
            >
              Find order
            </Button>
          </div>
        </form>
        {lookupError ? <p className="mt-3 text-sm text-danger">{lookupError}</p> : null}
      </Card>

      {!order && orderResults.length > 0 ? (
        <Card>
          <h2 className="text-sm font-semibold text-ink">
            {orderResults.length} orders found — select one to create a return
          </h2>
          <ul className="mt-3 divide-y divide-line">
            {orderResults.map((candidate) => (
              <li key={String(candidate.id)} className="flex items-center justify-between gap-3 py-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-ink">Order {candidate.name}</span>
                    {candidate.financial_status ? (
                      <Badge tone="neutral">{candidate.financial_status}</Badge>
                    ) : null}
                  </div>
                  <p className="mt-0.5 truncate text-xs text-ink-subtle">
                    {[
                      candidate.created_at ? formatDate(candidate.created_at) : null,
                      `${(candidate.line_items ?? []).length} item${
                        (candidate.line_items ?? []).length === 1 ? '' : 's'
                      }`,
                      formatCurrency(candidate.total_price),
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </div>
                <Button size="sm" variant="secondary" onClick={() => selectOrder(candidate)}>
                  Select
                </Button>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {order ? (
        <div className="flex flex-col gap-4">
          <Card>
            <div className="flex items-start justify-between gap-3">
              <div>
                {orderResults.length > 1 ? (
                  <button
                    type="button"
                    onClick={clearSelection}
                    className="mb-1 text-xs font-medium text-brand hover:underline"
                  >
                    ← Back to {orderResults.length} orders
                  </button>
                ) : null}
                <h2 className="text-sm font-semibold text-ink">Order {order.name}</h2>
                <p className="mt-0.5 text-xs text-ink-subtle">
                  {[
                    order.created_at ? `Placed ${formatDate(order.created_at)}` : null,
                    order.financial_status ? `Payment: ${order.financial_status}` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </div>
              <span className="text-sm font-medium text-ink">{formatCurrency(order.total_price)}</span>
            </div>

            <div className="mt-3 grid grid-cols-1 gap-3 rounded-lg bg-surface-muted p-3 text-xs sm:grid-cols-2">
              <div>
                <p className="font-medium text-ink-muted">Customer</p>
                <p className="mt-0.5 text-ink">
                  {[order.customer?.first_name, order.customer?.last_name].filter(Boolean).join(' ') || '—'}
                </p>
                <p className="text-ink-subtle">{order.email ?? order.customer?.email ?? '—'}</p>
                {order.customer?.phone ? <p className="text-ink-subtle">{order.customer.phone}</p> : null}
              </div>
              {order.shipping_address ? (
                <div>
                  <p className="font-medium text-ink-muted">Shipping address</p>
                  {order.shipping_address.name ? <p className="mt-0.5 text-ink">{order.shipping_address.name}</p> : null}
                  <p className="text-ink-subtle">
                    {[order.shipping_address.address1, order.shipping_address.address2].filter(Boolean).join(', ')}
                  </p>
                  <p className="text-ink-subtle">
                    {[
                      order.shipping_address.city,
                      order.shipping_address.province,
                      order.shipping_address.zip,
                      order.shipping_address.country,
                    ]
                      .filter(Boolean)
                      .join(', ')}
                  </p>
                  {order.shipping_address.phone ? <p className="text-ink-subtle">{order.shipping_address.phone}</p> : null}
                </div>
              ) : null}
            </div>

            <p className="mt-4 text-xs font-medium text-ink-muted">Select items to return</p>
            <ul className="mt-1 divide-y divide-line">
              {(order.line_items ?? []).map((lineItem) => {
                const lineItemId = String(lineItem.id);
                const selectedQty = selectedQuantities[lineItemId] ?? 0;
                const isSelected = selectedQty > 0;
                const maxQty = Math.max(0, lineItem.quantity - (lineItem.returnedQty ?? 0));
                const isFreebie = isFreebieLineItem(lineItem);
                const isReturnable = maxQty > 0 && !isFreebie;
                const imageSrc = lineItem.imageUrl ?? lineItem.image;
                const title =
                  lineItem.title.length > 100 ? `${lineItem.title.slice(0, 100)}…` : lineItem.title;
                return (
                  <li key={lineItemId} className="flex items-center gap-3 py-2.5">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleItem(lineItemId)}
                      disabled={!isReturnable}
                      className="h-4 w-4 accent-brand disabled:cursor-not-allowed disabled:opacity-40"
                      aria-label={`Select ${title}`}
                    />
                    {imageSrc ? (
                      <img
                        src={imageSrc}
                        alt=""
                        className="h-10 w-10 flex-shrink-0 rounded-lg border border-line object-cover"
                      />
                    ) : (
                      <span className="h-10 w-10 flex-shrink-0 rounded-lg bg-surface-sunken" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-ink">{title}</p>
                      <p className="text-xs text-ink-subtle">
                        {[lineItem.variant_title, lineItem.sku].filter(Boolean).join(' · ')}
                        {[lineItem.variant_title, lineItem.sku].filter(Boolean).length ? ' · ' : ''}
                        Ordered qty {lineItem.quantity}
                        {lineItem.returnedQty ? ` · ${lineItem.returnedQty} already returned` : ''}
                        {isFreebie ? ' · Free item' : ''}
                        {!isReturnable && !isFreebie && lineItem.returnStatus ? ` · Already ${humanizeEnum(lineItem.returnStatus)}` : ''}
                      </p>
                    </div>
                    {isSelected && maxQty > 1 ? (
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          aria-label="Decrease quantity"
                          className="flex h-7 w-7 items-center justify-center rounded-md border border-line text-ink-muted hover:bg-surface-muted disabled:opacity-40"
                          disabled={selectedQty <= 1}
                          onClick={() => setItemQuantity(lineItemId, selectedQty - 1, maxQty)}
                        >
                          −
                        </button>
                        <span className="w-8 text-center text-sm font-medium text-ink">{selectedQty}</span>
                        <button
                          type="button"
                          aria-label="Increase quantity"
                          className="flex h-7 w-7 items-center justify-center rounded-md border border-line text-ink-muted hover:bg-surface-muted disabled:opacity-40"
                          disabled={selectedQty >= maxQty}
                          onClick={() => setItemQuantity(lineItemId, selectedQty + 1, maxQty)}
                        >
                          +
                        </button>
                        <span className="ml-1 text-xs text-ink-subtle">/ {maxQty}</span>
                      </div>
                    ) : null}
                    <span className="text-sm font-medium text-ink">{formatCurrency(lineItem.price)}</span>
                  </li>
                );
              })}
            </ul>
          </Card>

          <Card>
            <h2 className="mb-3 text-sm font-semibold text-ink">Return details</h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <SelectField
                label="Reason"
                value={reasonSlug}
                onChange={(event) => setReasonSlug(event.target.value)}
                placeholder="Select a reason"
                options={reasons.map((reason) => ({ value: reason.slug, label: reason.label }))}
              />
              <SelectField
                label="Refund mode"
                value={refundMode}
                onChange={(event) => setRefundMode(event.target.value as RefundMode)}
                options={REFUND_MODE_OPTIONS.map((mode) => ({ value: mode, label: refundModeLabel(mode) }))}
              />
              <TextField
                label="Customer email"
                type="email"
                value={customerEmail}
                disabled
                className="disabled:cursor-not-allowed disabled:bg-surface-muted disabled:text-ink-subtle"
              />
              <TextField
                label="Customer name"
                value={customerName}
                disabled
                className="disabled:cursor-not-allowed disabled:bg-surface-muted disabled:text-ink-subtle"
              />
              <TextField
                label="Customer phone"
                type="tel"
                value={customerPhone}
                disabled
                className="disabled:cursor-not-allowed disabled:bg-surface-muted disabled:text-ink-subtle"
              />
            </div>
            { false && <div className="mt-3">
              <Toggle label="This is an exchange" checked={isExchange} onChange={setIsExchange} />
            </div>}
            <div className="mt-3">
              <TextArea label="Internal notes (optional)" value={internalNotes} onChange={(event) => setInternalNotes(event.target.value)} />
            </div>
          </Card>

          <div className="flex justify-end">
            <Button onClick={handleSubmit} isLoading={isSubmitting} disabled={!canSubmit}>
              Create return ({selectedItems.length} item{selectedItems.length === 1 ? '' : 's'})
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
